import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Charger } from '../types';
import { chargerService } from '../services/chargerService';
import { authService } from '../services/authService';
import { ChargerCard } from '../components/ChargerCard';
import { ChargerFilter } from '../components/ChargerFilter';
import { LiveUpdatesDrawer } from '../components/LiveUpdatesDrawer';
import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { useUserActivity } from '../hooks/useUserActivity';
import { Zap, WifiOff } from 'lucide-react';

type FilterType = 'all' | 'available' | 'busy' | 'offline' | 'queueable';

export function HomePage() {
  const { t, ready } = useTranslation();

  // Wait for i18n to be ready to prevent showing translation keys
  if (!ready) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f5f5f5'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '2rem'
        }}>
          <div style={{
            fontSize: '1.5rem',
            color: '#6b7280',
            marginBottom: '1rem'
          }}>⚡</div>
          <div style={{
            fontSize: '1.1rem',
            color: '#6b7280'
          }}>Loading ChargeQueue...</div>
        </div>
      </div>
    );
  }
  const [chargers, setChargers] = useState<Charger[]>([]);
  const [filteredChargers, setFilteredChargers] = useState<Charger[]>([]);
  const [filter, setFilter] = useState<FilterType>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track user activity to determine if auto-refresh should be enabled
  const { activity, refresh: refreshUserActivity } = useUserActivity();

  useEffect(() => {
    // Automatic kiosk cleanup when entering home page
    if (authService.isKioskUser()) {
      authService.ensureKioskMode();
    }
    
    loadChargers();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [chargers, filter]);

  // Set up auto-refresh for charger data - only when user is charging or in queue
  useAutoRefresh({
    enabled: true,
    onRefresh: () => loadChargers(true),
    shouldRefresh: () => {
      // Only auto-refresh if user is actively charging or waiting in a queue
      return activity.isCharging || activity.isInQueue;
    }
  });

  const loadChargers = async (isRefresh = false) => {
    try {
      console.log('🔄 loadChargers called with isRefresh:', isRefresh);
      if (isRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);
      const data = await chargerService.getChargers();
      console.log('🔄 Chargers data loaded:', data.length, 'chargers');
      setChargers(data);
    } catch (err) {
      console.error('🔄 Error loading chargers:', err);
      setError(err instanceof Error ? err.message : t('errors.network'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      console.log('🔄 loadChargers completed');
    }
  };

  const applyFilter = () => {
    if (!chargers || !Array.isArray(chargers)) {
      setFilteredChargers([]);
      return;
    }

    let filtered = chargers;

    switch (filter) {
      case 'available':
        filtered = chargers.filter(c => c.online && c.status === 'idle');
        break;
      case 'busy':
        filtered = chargers.filter(c => c.online && c.status !== 'idle');
        break;
      case 'offline':
        filtered = chargers.filter(c => !c.online);
        break;
      case 'queueable':
        filtered = chargers.filter(c => c.canJoinQueue && c.queueLength < 4);
        break;
      default:
        filtered = chargers;
    }

    setFilteredChargers(filtered);
  };

  const handleChargerAction = async () => {
    // Reload chargers after any action and refresh user activity
    await Promise.all([
      loadChargers(true),
      refreshUserActivity()
    ]);
  };

  if (isLoading && !isRefreshing) {
    return (
      <div className="modern-loading">
        <div className="loading-spinner">
          <Zap className="animate-spin" size={32} />
        </div>
        <p className="loading-text">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-error">
        <div className="error-card">
          <div className="error-icon">
            <WifiOff size={48} />
          </div>
          <h2 className="error-title">{t('errors.generic')}</h2>
          <p className="error-message">{error}</p>
          <button className="btn-modern btn-primary-modern" onClick={() => loadChargers()}>
            <Zap size={16} />
            {t('common.refresh')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-home-page">
      {/* Impersonation Banner - shown when admin is impersonating a user */}
      <ImpersonationBanner />

      <ChargerFilter 
        currentFilter={filter} 
        onFilterChange={setFilter}
        chargers={chargers}
      />

      {/* Auto-refresh indicator - only show when user is charging or in queue */}
      {(activity.isCharging || activity.isInQueue) && (
        <div className="auto-refresh-indicator" style={{
          fontSize: '0.8rem',
          color: '#666',
          textAlign: 'center',
          marginBottom: '1rem',
          opacity: 0.7
        }}>
          🔄 Auto-refreshing data
          {activity.isCharging && ' (charging)'}
          {activity.isInQueue && !activity.isCharging && ' (in queue)'}
        </div>
      )}

      <div className="modern-chargers-grid">
        {filteredChargers.length === 0 ? (
          <div className="modern-empty-state">
            <div className="empty-icon">
              <Zap size={64} />
            </div>
            <h3 className="empty-title">{t('home.filters.all')}</h3>
            <p className="empty-description">No chargers match the current filter.</p>
            {filter !== 'all' && (
              <button 
                className="btn-modern btn-secondary-modern" 
                onClick={() => setFilter('all')}
              >
                {t('home.filters.all')}
              </button>
            )}
          </div>
        ) : (
          filteredChargers.map(charger => (
            <ChargerCard
              key={charger.id}
              charger={charger}
              onAction={handleChargerAction}
            />
          ))
        )}
      </div>

      {/* Live Updates Drawer - positioned at bottom of screen */}
      <LiveUpdatesDrawer onUpdate={() => loadChargers(true)} />
    </div>
  );
}